import { useEffect, useCallback, useRef } from 'react';
import analytics from '@/utils/analytics';

/**
 * Custom hook for analytics tracking
 * Provides convenient methods for tracking user behavior and performance
 */
export const useAnalytics = () => {
  return {
    // User behavior tracking
    trackClick: analytics.click,
    trackFormSubmit: analytics.formSubmit,
    trackFormInteraction: analytics.formInteraction,
    trackSearch: analytics.search,
    trackFilter: analytics.filter,
    trackSort: analytics.sort,
    trackModal: analytics.modal,
    trackTabChange: analytics.tabChange,
    trackNavigation: analytics.navigation,
    trackDownload: analytics.download,
    trackError: analytics.error,

    // Performance tracking
    trackAPIPerformance: analytics.apiPerformance,
    startMeasure: analytics.startMeasure,
    endMeasure: analytics.endMeasure,

    // Admin-specific tracking
    trackAdminAction: analytics.adminAction,
    trackTableAction: analytics.tableAction,
    trackDashboardWidget: analytics.dashboardWidget,
  };
};

/**
 * Hook to track page views automatically
 */
export const usePageTracking = (pageName?: string) => {
  useEffect(() => {
    const page = pageName || window.location.pathname;
    analytics.pageView(page);
    analytics.pageLoadTime(page);
  }, [pageName]);
};

/**
 * Hook to track time spent on page
 */
export const useTimeTracking = (pageName?: string) => {
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    startTimeRef.current = Date.now();

    return () => {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (timeSpent > 0) {
        analytics.timeOnPage(timeSpent, pageName);
      }
    };
  }, [pageName]);
};

/**
 * Hook to track scroll depth
 */
export const useScrollTracking = (pageName?: string) => {
  const trackedDepths = useRef<Set<number>>(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100);

      // Track at 25%, 50%, 75%, and 100%
      const milestones = [25, 50, 75, 100];
      milestones.forEach((milestone) => {
        if (scrollPercentage >= milestone && !trackedDepths.current.has(milestone)) {
          trackedDepths.current.add(milestone);
          analytics.scrollDepth(milestone, pageName);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageName]);
};

/**
 * Hook to track component render performance
 */
export const useRenderTracking = (componentName: string) => {
  const renderStartTime = useRef<number>(performance.now());

  useEffect(() => {
    const renderTime = performance.now() - renderStartTime.current;
    if (renderTime > 0) {
      analytics.componentRender(componentName, renderTime);
    }
  });
};

/**
 * Hook to track API calls with performance metrics
 */
export const useAPITracking = () => {
  const trackAPI = useCallback((
    endpoint: string,
    method: string,
    startTime: number,
    statusCode: number,
    success: boolean
  ) => {
    const responseTime = Date.now() - startTime;
    analytics.apiPerformance(endpoint, method, responseTime, statusCode, success);
  }, []);

  return { trackAPI };
};

/**
 * Hook for tracking form interactions
 */
export const useFormTracking = (formName: string) => {
  const trackSubmit = useCallback(
    (success: boolean, errorMessage?: string) => {
      analytics.formSubmit(formName, success, errorMessage);
    },
    [formName]
  );

  const trackFieldInteraction = useCallback(
    (fieldName: string, action: 'focus' | 'blur' | 'change') => {
      analytics.formInteraction(formName, fieldName, action);
    },
    [formName]
  );

  return {
    trackSubmit,
    trackFieldInteraction,
  };
};

/**
 * Hook for tracking modal interactions
 */
export const useModalTracking = (modalName: string) => {
  useEffect(() => {
    // Track modal open
    analytics.modal(modalName, 'open');

    return () => {
      // Track modal close on unmount
      analytics.modal(modalName, 'close');
    };
  }, [modalName]);

  const trackConfirm = useCallback(() => {
    analytics.modal(modalName, 'confirm');
  }, [modalName]);

  const trackCancel = useCallback(() => {
    analytics.modal(modalName, 'cancel');
  }, [modalName]);

  return {
    trackConfirm,
    trackCancel,
  };
};

/**
 * Hook for tracking table interactions
 */
export const useTableTracking = (tableName: string) => {
  const trackAction = useCallback(
    (
      action: 'view' | 'create' | 'edit' | 'delete' | 'export' | 'import' | 'bulk_action',
      rowCount?: number
    ) => {
      analytics.tableAction(tableName, action, rowCount);
    },
    [tableName]
  );

  return { trackAction };
};

/**
 * Hook for tracking dashboard widget interactions
 */
export const useWidgetTracking = (widgetName: string) => {
  useEffect(() => {
    // Track widget view on mount
    analytics.dashboardWidget(widgetName, 'view');
  }, [widgetName]);

  const trackRefresh = useCallback(() => {
    analytics.dashboardWidget(widgetName, 'refresh');
  }, [widgetName]);

  const trackConfigure = useCallback(() => {
    analytics.dashboardWidget(widgetName, 'configure');
  }, [widgetName]);

  const trackExpand = useCallback(() => {
    analytics.dashboardWidget(widgetName, 'expand');
  }, [widgetName]);

  const trackCollapse = useCallback(() => {
    analytics.dashboardWidget(widgetName, 'collapse');
  }, [widgetName]);

  return {
    trackRefresh,
    trackConfigure,
    trackExpand,
    trackCollapse,
  };
};

export default useAnalytics;
